import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useViewContext } from "@/contexts/ViewContext";
import { Button } from "@/components/ui/button";
import { NotificationBell } from "@/components/owner/NotificationBell";
import { Home } from "lucide-react";
import { MobileNav } from "@/components/layout/MobileNav";
import { PropertySwitcher } from "@/components/PropertySwitcher";
import { ProfileDropdown } from "@/components/ProfileDropdown";
import { cn } from "@/lib/utils";

interface NavbarProps {
  /** On property detail (mobile): show this instead of MobileNav so one hamburger opens property tabs. */
  leftSlot?: React.ReactNode;
  /** When true, use position fixed so navbar never scrolls (use on property detail with fixed sidebar). */
  fixed?: boolean;
  /** Merged onto the root `<nav>` (e.g. cleaner turnover chrome overrides). */
  className?: string;
}

const Navbar = ({ leftSlot, fixed, className }: NavbarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const onPropertiesHome = location.pathname === "/owner/properties";
  const { user, profile } = useAuth();
  const { viewContext } = useViewContext();
  const cleanerChrome = viewContext === "cleaner";

  const getHomePath = () => {
    if (cleanerChrome) return "/worker/schedule";
    return "/owner/properties";
  };

  return (
    <nav
      className={cn(
        fixed
          ? "fixed top-0 left-0 right-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
          : "sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
        className
      )}
    >
      <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        <div className="flex items-center justify-between h-14">
          <div className="flex items-center gap-2">
            {!cleanerChrome && !onPropertiesHome ? (
              leftSlot ? (
                <>
                  <span className="lg:hidden flex items-center">{leftSlot}</span>
                  <span className="hidden lg:block">
                    <MobileNav />
                  </span>
                </>
              ) : (
                <MobileNav />
              )
            ) : null}
            {user && profile && !cleanerChrome && !onPropertiesHome && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(getHomePath())}
                className="h-8 px-2 text-muted-foreground hover:text-foreground"
                aria-label="Home"
              >
                <Home className="h-4 w-4" />
                <span className="ml-1.5 hidden xs:inline">Home</span>
              </Button>
            )}
            <button
              type="button"
              onClick={() => user && profile ? navigate(getHomePath()) : undefined}
              className={cn(
                "truncate text-sm font-semibold hover:opacity-80 transition-opacity cursor-pointer sm:text-base",
                onPropertiesHome ? "text-[#64748b]" : "text-primary",
              )}
            >
              Stravo
            </button>
          </div>

          {user && profile && !cleanerChrome && (
            <>
              <div className="hidden md:flex items-center gap-3">
                <PropertySwitcher />
                <NotificationBell />
                <ProfileDropdown />
              </div>
              <div className="flex md:hidden items-center gap-1">
                <ProfileDropdown />
              </div>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
